import React, { useEffect, useState } from 'react';
import { managerAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Building2, MapPin, DoorOpen, AlertCircle, Loader2, Plus, Pencil, Search, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ManagerProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [propsRes, dashRes] = await Promise.all([
        managerAPI.getProperties(),
        managerAPI.getDashboard()
      ]);
      setProperties(propsRes.data);
      setPermissions(dashRes.data.permissions || {});
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingProperty(null);
    setForm({ name: '', address: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (prop) => {
    setEditingProperty(prop);
    setForm({ name: prop.name, address: prop.address, description: prop.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      toast.error('Name and address are required');
      return;
    }
    setSaving(true);
    try {
      if (editingProperty) {
        await managerAPI.updateProperty(editingProperty.property_id, form);
        toast.success('Property updated');
      } else {
        await managerAPI.createProperty(form);
        toast.success('Property created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Error</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manager-properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Properties</h1>
          <p className="text-muted-foreground">Properties you have access to manage</p>
        </div>
        {permissions.create_properties && (
          <Button onClick={openCreate} data-testid="create-property-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Property
          </Button>
        )}
      </div>

      {properties.length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, address…"
            className="pl-9 pr-9"
            data-testid="manager-properties-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              data-testid="manager-properties-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {(() => {
        const q = search.trim().toLowerCase();
        const filtered = !q
          ? properties
          : properties.filter((p) =>
              [p.name, p.address, p.description].filter(Boolean).join(' ').toLowerCase().includes(q)
            );
        if (properties.length === 0) {
          return (
            <Card>
              <CardContent className="p-12 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No Properties Assigned</h3>
                <p className="text-muted-foreground">
                  You don't have any properties assigned yet. Contact your landlord to get access.
                </p>
              </CardContent>
            </Card>
          );
        }
        if (filtered.length === 0) {
          return (
            <Card>
              <CardContent className="p-12 text-center">
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No matches</h3>
                <p className="text-muted-foreground">No properties match "{search}".</p>
              </CardContent>
            </Card>
          );
        }
        return (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((property) => (
            <Card key={property.property_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{property.name}</CardTitle>
                  <div className="flex items-center gap-2">
                    {permissions.edit_properties && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(property)} data-testid={`edit-property-${property.property_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    <Badge variant="outline">
                      <DoorOpen className="w-3 h-3 mr-1" />
                      {property.total_units} units
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{property.address}</span>
                  </div>
                  {property.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{property.description}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        );
      })()}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProperty ? 'Edit Property' : 'Add Property'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Property Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sunrise Dormitory" data-testid="property-name-input" />
            </div>
            <div>
              <Label>Address *</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full address" data-testid="property-address-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional description" data-testid="property-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-property-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingProperty ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
